import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { Snackbar, Alert, AlertColor, Slide, SlideProps } from '@mui/material';

interface Notification {
  id: string;
  message: string;
  severity: AlertColor;
  duration?: number;
}

interface NotificationContextType {
  showNotification: (message: string, severity?: AlertColor, duration?: number) => void;
  hideNotification: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="down" />;
}

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Stabil (üres deps, funkcionális setState) – a showNotification erre épül
  const hideNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((notification) => notification.id !== id));
  }, []);

  const showNotification = useCallback(
    (message: string, severity: AlertColor = 'info', duration: number = 4000) => {
      const id = `notification-${Date.now()}-${Math.random()}`;
      const newNotification: Notification = {
        id,
        message,
        severity,
        duration,
      };

      setNotifications((prev) => [...prev, newNotification]);

      // Auto-dismiss after duration
      if (duration > 0) {
        setTimeout(() => {
          hideNotification(id);
        }, duration);
      }
    },
    [hideNotification]
  );

  // Stabil context-érték: egy értesítés megjelenése/eltűnése ne renderelje
  // újra az összes useNotification-fogyasztót (a függvények már stabilak).
  const value = useMemo(
    () => ({ showNotification, hideNotification }),
    [showNotification, hideNotification],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
      {/* Render all notifications */}
      {notifications.map((notification, index) => (
        <Snackbar
          key={notification.id}
          open={true}
          autoHideDuration={notification.duration}
          onClose={() => hideNotification(notification.id)}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          TransitionComponent={SlideTransition}
          sx={{
            top: { xs: 16, sm: 24 },
            // Stack multiple notifications
            mt: index > 0 ? `${index * 72}px` : 0,
          }}
        >
          <Alert
            onClose={() => hideNotification(notification.id)}
            severity={notification.severity}
            variant="filled"
            sx={{
              width: '100%',
              minWidth: { xs: 300, sm: 400 },
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              fontWeight: 500,
              fontSize: '0.9375rem',
              // Többsoros hibák (mezőnként egy sor) sortöréssel jelenjenek meg.
              whiteSpace: 'pre-line',
            }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      ))}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
